import { RestartAltRounded } from '@mui/icons-material';
import { Badge, Box, Button, Input, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider, PickersDay } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { setTransportSelectedData } from 'app/actions/map';
import { ArrowLeft, ArrowRight } from 'app/icons';
import { transportBroadcastRecordLoad } from 'app/store/map';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const MapTransportPicker = function () {
  const theme = useTheme();
  const dispatch = useDispatch();
  const firstUpdate = useRef(true);
  const userSession = useSelector((state) => state.session.session);
  const broadcast = useSelector((state) => state.map.transportSelectedItem.broadcast);
  const selectedDate = useSelector((state) => state.map.transportSelectedDate);
  const [value, setValue] = useState([moment(selectedDate[0]), moment(selectedDate[1])]);
  const transportConditionSelectedDatetime = useSelector((state) => state.map.transportConditionSelectedDatetime);

  const isNextDayDisabled = useMemo(() => {
    return moment().startOf('day').isSame(value[0]);
  }, [value]);

  const isWithinLast24Hours = (date) => {
    return moment().diff(date, 'hours') <= 24;
  };

  const handleChangeFrom = (newValue) => {
    if (newValue.isValid()) {
      setValue([newValue, null]);
    }
  };

  const handleChangeTo = (newValue, keyboardInputValue) => {
    if (newValue.isValid()) {
      setValue([value[0], keyboardInputValue ? newValue : newValue.endOf('day')]);
    }
  };

  const handleChangePrevDay = () => {
    setValue([moment(value[0]).startOf('day').subtract(1, 'days'), moment(value[0]).startOf('day').subtract(1, 'days').endOf('day')]);
  };

  const handleChangeNextDay = () => {
    setValue([moment(value[0]).startOf('day').add(1, 'days'), moment(value[0]).startOf('day').add(1, 'days').endOf('day')]);
  };

  const handleChangeOneDayAgo = () => {
    setValue([moment().startOf('day').subtract(1, 'days'), moment().endOf('day').subtract(1, 'days')]);
  };

  const handleChangeTwoDaysAgo = () => {
    setValue([moment().startOf('day').subtract(2, 'days'), moment().endOf('day').subtract(2, 'days')]);
  };

  const handleChangeCurrentDay = () => {
    setValue([moment().startOf('day'), moment().endOf('day')]);
  };

  const handleReset = () => {
    setValue([moment().startOf('day'), moment().endOf('day').add(8, 'hours').add(1, 'seconds').add(1, 'minutes')]);
  };

  const adjustRangeToEvent = (eventDate) => {
    setValue([moment(eventDate).startOf('day'), moment(eventDate).endOf('day').add(8, 'hours').add(1, 'seconds').add(1, 'minutes')]);
  };

  const handleMonthChange = (value) => {
    if (broadcast.terid.length !== 0) {
      dispatch(transportBroadcastRecordLoad(broadcast.terid, value))
        .then((response) => response.length)
        .catch();
    }
  };

  const generateRecords = () => {
    const items = [];

    if (broadcast.previousRecords.length !== 0) {
      broadcast.previousRecords.forEach((record) => {
        items.push(moment(record.date).format('DD.MM'));
      });
    }

    return items;
  };

  const generateEvents = () => {
    const items = [];

    if (broadcast.previousEvents.length !== 0) {
      broadcast.previousEvents.forEach((record) => {
        items.push(moment(record.gpstime).format('DD.MM'));
      });
    }

    return items;
  };

  const renderDay = (day, selectedDays, pickersDayProps) => {
    const isSelected = generateEvents().indexOf(moment(day).format('DD.MM')) !== -1;
    const isVideoExist = generateRecords().indexOf(moment(day).format('DD.MM')) !== -1;

    return (
      <Badge
        key={day.toString()}
        overlap='circular'
        badgeContent={isSelected ? '' : undefined}
        sx={(theme) => ({
          ...(isSelected && {
            '.MuiBadge-badge': {
              width: '7px',
              height: '7px',
              background: theme.palette.primary.main,
              borderRadius: '50%',
              minWidth: '7px',
              padding: '0',
            },
          }),
        })}>
        <PickersDay sx={isVideoExist ? { background: theme.palette.grey.light } : {}} {...pickersDayProps} />
      </Badge>
    );
  };

  useEffect(() => {
    if (!firstUpdate.current && value.every((date) => date)) {
      dispatch(setTransportSelectedData(value));
    }
  }, [value]);

  useEffect(() => {
    const eventDate = moment(transportConditionSelectedDatetime);
    if (transportConditionSelectedDatetime && !firstUpdate.current) {
      if (!moment(value[0]).isSame(eventDate, 'day') && isWithinLast24Hours(transportConditionSelectedDatetime)) {
        adjustRangeToEvent(transportConditionSelectedDatetime);
      }
    }
  }, [transportConditionSelectedDatetime]);

  useEffect(() => {
    firstUpdate.current = false;
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={moment.locale('ru')}>
      <Box
        component='div'
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          '.MuiInput-root': {
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: theme.palette.grey.light,
            bgcolor: 'background.paper',
            padding: '0 4px 0 8px',
            userSelect: 'none',
            '&:hover': {
              cursor: 'pointer',
            },
            '.MuiInput-input': {
              height: '100%',
              lineHeight: '1',
              fontSize: '12px',
              cursor: 'pointer',
              textAlign: 'left',
              padding: '6px 0 5px',
              width: 'auto',
              maxWidth: '112px',
            },
          },
          '.MuiButton-root': {
            padding: '3px 5px',
            minWidth: 'auto',
            background: '#ffffff',
            border: '1px solid',
            borderColor: '#ddde',
            color: '#000000',
            fontSize: '11px',
            '&:hover': {
              background: '#ffffff',
            },
            svg: {
              fontSize: '19px',
            },
          },
        })}>
        <DateTimePicker
          value={value[0]}
          views={['day']}
          inputFormat='DD.MM.YYYY HH:mm:ss'
          disableFuture
          minDate={userSession.time_limit ? moment(userSession.time_limit) : undefined}
          renderDay={renderDay}
          onChange={handleChangeFrom}
          onMonthChange={handleMonthChange}
          renderInput={({ inputRef, inputProps, InputProps }) => (
            <Input
              ref={inputRef}
              onClick={InputProps.endAdornment?.props.children.props.onClick}
              disableUnderline
              inputProps={{
                ...inputProps,
                placeholder: 'Выберите дату',
              }}
            />
          )}
          PaperProps={{
            sx: (theme) => ({
              marginTop: '9px',
              boxShadow: '0px 3px 6px ' + alpha(theme.palette.grey.main, 0.12),
              '.MuiCalendarOrClockPicker-root > div': {
                width: '234px',
                '.MuiCalendarPicker-root': {
                  width: '100%',
                },
                '.MuiPickersCalendarHeader-root': {
                  margin: '6px 0',
                },
                '.MuiPickersCalendarHeader-labelContainer': {
                  fontSize: '14px',
                  '.MuiPickersCalendarHeader-switchViewButton': {
                    display: 'none',
                  },
                },
              },
              '.MuiPickersArrowSwitcher-root': {
                padding: '8px 12px',
                '.MuiButtonBase-root': {
                  width: '26px',
                  height: '26px',
                },
              },
              '.MuiDayPicker-weekDayLabel, .MuiPickersDay-root': {
                width: '26px',
                height: '26px',
              },
              '.PrivatePickersSlideTransition-root': {
                minWidth: '234px',
                minHeight: '182px',
              },
              '.MuiDateRangePickerViewDesktop-container:not(:last-of-type)': {
                borderWidth: '1px',
              },
              '.MuiDateRangePickerDay-dayInsideRangeInterval, .MuiPickersDay-today.Mui-selected': {
                color: theme.palette.primary.contrastText,
              },
              '.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                '&:not(.MuiDateRangePickerDay-rangeIntervalDayHighlightStart):not(.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd)': {
                  borderRadius: 0,
                },
                '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                    borderRadius: '50%',
                  },
                },
              },
              '.MuiDateRangePickerDay-rangeIntervalDayPreview': {
                borderColor: 'transparent',
              },
            }),
          }}
        />
        <Typography variant='body1'>по</Typography>
        <DateTimePicker
          value={value[1]}
          views={['day']}
          inputFormat='DD.MM.YYYY HH:mm:ss'
          renderDay={renderDay}
          minDate={moment(value[0]).endOf('day')}
          maxDate={moment(value[0]).add(2, 'days').isAfter(moment(), 'day') ? moment().endOf('day') : moment(value[0]).add(2, 'days')}
          onChange={handleChangeTo}
          onMonthChange={handleMonthChange}
          renderInput={({ inputRef, inputProps, InputProps }) => (
            <Input
              ref={inputRef}
              onClick={InputProps.endAdornment?.props.children.props.onClick}
              disableUnderline
              inputProps={{
                ...inputProps,
                placeholder: 'Выберите дату',
              }}
            />
          )}
          PaperProps={{
            sx: (theme) => ({
              marginTop: '9px',
              boxShadow: '0px 3px 6px ' + alpha(theme.palette.grey.main, 0.12),
              '.MuiCalendarOrClockPicker-root > div': {
                width: '234px',
                '.MuiCalendarPicker-root': {
                  width: '100%',
                },
                '.MuiPickersCalendarHeader-root': {
                  margin: '6px 0',
                },
                '.MuiPickersCalendarHeader-labelContainer': {
                  fontSize: '14px',
                  '.MuiPickersCalendarHeader-switchViewButton': {
                    display: 'none',
                  },
                },
              },
              '.MuiPickersArrowSwitcher-root': {
                padding: '8px 12px',
                '.MuiButtonBase-root': {
                  width: '26px',
                  height: '26px',
                },
              },
              '.MuiDayPicker-weekDayLabel, .MuiPickersDay-root': {
                width: '26px',
                height: '26px',
              },
              '.PrivatePickersSlideTransition-root': {
                minWidth: '234px',
                minHeight: '182px',
              },
              '.MuiDateRangePickerViewDesktop-container:not(:last-of-type)': {
                borderWidth: '1px',
              },
              '.MuiDateRangePickerDay-dayInsideRangeInterval, .MuiPickersDay-today.Mui-selected': {
                color: theme.palette.primary.contrastText,
              },
              '.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                '&:not(.MuiDateRangePickerDay-rangeIntervalDayHighlightStart):not(.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd)': {
                  borderRadius: 0,
                },
                '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                    borderRadius: '50%',
                  },
                },
              },
              '.MuiDateRangePickerDay-rangeIntervalDayPreview': {
                borderColor: 'transparent',
              },
            }),
          }}
        />
        <Button onClick={handleChangePrevDay}>
          <ArrowLeft height='24px' viewBox='0 -960 960 960' width='24px' />
        </Button>
        <Button disabled={isNextDayDisabled} onClick={handleChangeNextDay}>
          <ArrowRight height='24px' viewBox='0 -960 960 960' width='24px' />
        </Button>
        <Button onClick={handleChangeOneDayAgo}>-1д</Button>
        <Button onClick={handleChangeTwoDaysAgo}>-2д</Button>
        <Button onClick={handleChangeCurrentDay}>24ч</Button>
        <Button onClick={handleReset}>
          <RestartAltRounded fontSize='small' />
        </Button>
      </Box>
    </LocalizationProvider>
  );
};

export default MapTransportPicker;
